import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/feature/SEOHead";
import {
  categories,
  featuredMovies,
  articleList,
  testimonials,
  faqs,
} from "@/mocks/movies";
import { useEffect, useState } from "react";

export default function Home() {
  const [navbarBg, setNavbarBg] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navbar");
      if (navbar) {
        if (window.scrollY > 60) {
          navbar.classList.add("bg-white/95", "backdrop-blur-sm", "border-b", "border-foreground-200/50");
          navbar.classList.remove("bg-transparent");
        } else {
          navbar.classList.remove("bg-white/95", "backdrop-blur-sm", "border-b", "border-foreground-200/50");
          navbar.classList.add("bg-transparent");
        }
      }
      setNavbarBg(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % featuredMovies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const siteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "8090电影网",
    url: "https://8090movie.com",
    description:
      "8090电影网怀旧影视专区，收录80后经典电影、90后电视剧、老电影在线观看，重温童年回忆剧，唤起青春记忆的专属片库。",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://8090movie.com/search?q={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "8090电影网",
    url: "https://8090movie.com",
    description:
      "8090电影网怀旧影视专区，专注于80后经典电影和90后电视剧的怀旧影视内容平台。",
    sameAs: [],
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((faq) => ({
      "@type": "Question",
      name: faq.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="8090电影网：80后经典电影与90后电视剧怀旧影视 | 老电影在线童年回忆剧"
        description="8090电影网怀旧影视专区，收录80后经典电影、90后电视剧、老电影在线观看，重温童年回忆剧，唤起青春记忆的专属片库。"
        keywords="8090电影网,80后经典电影,90后电视剧,怀旧影视,老电影在线,童年回忆剧,经典老片,青春记忆,怀旧电影,老电视剧,8090后影视,回忆杀"
        canonicalUrl="https://8090movie.com"
        ogImage="https://readdy.ai/api/search-image?query=A%20warm%20nostalgic%20vintage%20film%20projector%20with%20golden%20light%20glowing%20on%20an%20old%20wooden%20table%2C%20surrounded%20by%20classic%20film%20reels%2C%20soft%20sepia%20tone%2C%20cinematic%20atmosphere%2C%20warm%20amber%20colors%2C%20nostalgic%20mood%2C%20no%20text&width=1200&height=630&seq=home-og-01&orientation=landscape"
        jsonLd={siteJsonLd}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />

      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center transition-all duration-1000"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=A%20nostalgic%20warm-toned%20vintage%20cinema%20lobby%20with%20classic%20movie%20posters%20on%20walls%2C%20old%20film%20projector%20emitting%20warm%20golden%20light%2C%20red%20velvet%20curtains%2C%20retro%20theater%20seats%2C%20soft%20sepia%20and%20amber%20atmosphere%2C%20cinematic%20lighting%2C%20dreamy%20nostalgic%20mood%2C%20no%20text%20or%20lettering%2C%20clean%20elegant%20composition&width=1800&height=1200&seq=hero-bg-8090-01&orientation=landscape)`,
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500/20 backdrop-blur-sm rounded-full border border-primary-400/30 mb-6">
                <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse" />
                <span className="text-primary-200 text-sm">怀旧影视专区 · 重温光影记忆</span>
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
                8090电影网
              </h1>
              <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                收录<strong>80后经典电影</strong>、<strong>90后电视剧</strong>，老电影在线重温童年回忆剧，唤起青春记忆的专属片库。每一帧画面，都是一段回不去的旧时光。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/80s-movies"
                  onClick={scrollToTop}
                  className="px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
                >
                  <i className="ri-film-line mr-2"></i>开始怀旧之旅
                </Link>
                <Link
                  to="/about"
                  onClick={scrollToTop}
                  className="px-8 py-3 bg-white/10 backdrop-blur-sm text-white font-medium rounded-full hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer text-sm border border-white/20"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
            {featuredMovies.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentSlide(idx)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  idx === currentSlide ? "w-6 bg-primary-400" : "bg-white/40 hover:bg-white/60"
                }`}
                aria-label={`切换到第${idx + 1}张`}
              />
            ))}
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                探索怀旧影视
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                按分类浏览，找到属于你那个年代的经典影视记忆
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={scrollToTop}
                  className="group flex flex-col items-center p-4 md:p-5 bg-white rounded-xl border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-primary-50 rounded-xl mb-3 group-hover:bg-primary-100 transition-colors">
                    <i className={`${cat.icon} text-xl md:text-2xl text-primary-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-800 text-center whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 mt-1 text-center line-clamp-2">
                    {cat.description.slice(0, 20)}...
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Carousel */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950">
                  精选推荐
                </h2>
                <p className="text-foreground-600 text-sm mt-1">编辑精选，必看经典</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentSlide(
                      (prev) =>
                        (prev - 1 + featuredMovies.length) % featuredMovies.length
                    )
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-background-300 hover:border-primary-400 transition-colors cursor-pointer"
                  aria-label="上一个"
                >
                  <i className="ri-arrow-left-s-line text-foreground-700"></i>
                </button>
                <button
                  onClick={() =>
                    setCurrentSlide((prev) => (prev + 1) % featuredMovies.length)
                  }
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-white border border-background-300 hover:border-primary-400 transition-colors cursor-pointer"
                  aria-label="下一个"
                >
                  <i className="ri-arrow-right-s-line text-foreground-700"></i>
                </button>
              </div>
            </div>

            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {featuredMovies.map((movie) => (
                  <div key={movie.id} className="w-full flex-shrink-0">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 bg-white rounded-2xl overflow-hidden border border-background-200">
                      <div className="relative h-64 md:h-80 lg:h-full min-h-[280px]">
                        <img
                          src={movie.imageUrl}
                          alt={`${movie.title} 经典剧照`}
                          title={`${movie.title} - 8090电影网怀旧影视`}
                          className="absolute inset-0 w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-6 md:p-8 flex flex-col justify-center">
                        <div className="flex items-center gap-3 mb-3">
                          <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full">
                            {movie.year}
                          </span>
                          <span className="flex items-center gap-1 text-sm text-foreground-600">
                            <i className="ri-star-fill text-accent-500 text-sm"></i>
                            {movie.rating}
                          </span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-2">
                          {movie.title}
                        </h3>
                        <p className="text-foreground-600 text-sm leading-relaxed mb-4 line-clamp-3">
                          {movie.excerpt}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mb-5">
                          {movie.cast.slice(0, 4).map((actor) => (
                            <span
                              key={actor}
                              className="px-2 py-0.5 bg-background-100 text-foreground-600 text-xs rounded"
                            >
                              {actor}
                            </span>
                          ))}
                        </div>
                        <Link
                          to={`/article/${movie.id}`}
                          onClick={scrollToTop}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors w-fit whitespace-nowrap cursor-pointer"
                        >
                          查看详情
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Article List */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                最新怀旧文章
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                每一篇文章都是一段珍贵的回忆，带你回到那个纯真年代
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {articleList.map((article) => (
                <article key={article.id} className="group bg-white rounded-xl border border-background-200 overflow-hidden hover:border-primary-300 transition-all cursor-pointer">
                  <Link to={`/article/${article.id}`} onClick={scrollToTop} className="block">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={`${article.title} 经典剧照`}
                        title={`${article.title} - 8090电影网怀旧影视`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 bg-primary-500 text-white text-xs font-medium rounded-full">
                          {article.year}
                        </span>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">
                          {categories.find((c) => c.slug === article.category)?.name}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-accent-600">
                          <i className="ri-star-fill text-xs"></i>
                          {article.rating}
                        </span>
                      </div>
                      <h3 className="font-semibold text-foreground-950 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors text-sm">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                影迷口碑
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
                听听同为怀旧影迷的他们怎么说
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {testimonials.map((t, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-background-200 p-5 md:p-6"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 font-semibold text-sm">
                      {t.avatar}
                    </div>
                    <div>
                      <div className="font-medium text-foreground-900 text-sm">{t.name}</div>
                      <div className="text-xs text-foreground-500">{t.year}</div>
                    </div>
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">
                    "{t.comment}"
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="w-full bg-background-50 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                常见问题
              </h2>
              <p className="text-foreground-600 text-sm">关于8090电影网，你可能想问的</p>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, idx) => (
                <details
                  key={idx}
                  className="group bg-white rounded-xl border border-background-200 overflow-hidden"
                >
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h3 className="font-medium text-foreground-900 pr-4 text-sm">
                      {faq.q}
                    </h3>
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 rounded-full bg-background-100 group-open:bg-primary-100 transition-colors">
                      <i className="ri-add-line text-sm text-foreground-600 group-open:hidden"></i>
                      <i className="ri-subtract-line text-sm text-primary-600 hidden group-open:block"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-foreground-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full relative py-16 md:py-20 overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url(https://readdy.ai/api/search-image?query=A%20warm%20nostalgic%20movie%20theater%20with%20soft%20golden%20lighting%2C%20empty%20red%20seats%2C%20large%20cinema%20screen%20emitting%20warm%20glow%2C%20retro%20atmosphere%2C%20sepia%20and%20amber%20tones%2C%20cinematic%20composition%2C%20deep%20shadows%2C%20no%20text%2C%20elegant%20and%20warm%20mood&width=1800&height=800&seq=cta-bg-8090-01&orientation=landscape)`,
            }}
          />
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative z-10 text-center px-4 md:px-6 max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              开启你的怀旧之旅
            </h2>
            <p className="text-white/70 text-sm mb-8 leading-relaxed">
              8090电影网怀旧影视专区，收录80后经典电影、90后电视剧。每一帧画面，都是回不去的青春。
            </p>
            <Link
              to="/category/80s-movies"
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-play-circle-line text-lg"></i>
              立即探索
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}